if (!customElements.get("product-form")) {
  customElements.define(
    "product-form",
    class ProductForm extends HTMLElement {
      constructor() {
        super();
        this.onSubmitHandler = this.onSubmitHandler.bind(this);
        this.form = this.querySelector("form");
        this.form.querySelector("[name=id]").disabled = false;
        this.form.addEventListener("submit", throttle(this.onSubmitHandler, 1000));
        this.cart = document.querySelector("cart-notification") || document.querySelector("cart-drawer");
        this.submitButton = this.querySelector('[type="submit"]');
        if (!!throttle && this.submitButton)
          this.submitButton.addEventListener("click", throttle(this.onSubmitHandler, 1000));

        if (document.querySelector("cart-drawer") && this.submitButton) {
          this.submitButton.setAttribute("aria-haspopup", "dialog");
        }

        document.addEventListener("DOMContentLoaded", () => {
          document.body.addEventListener("change", (event) => {
            if (event.target && event.target.id == "fp-gift-card-amount") {
              this.handleGiftCardDropdownChange(event.target);
            }
            // Reset dropdown when a gift card radio button is selected
            if (event.target?.type === "radio" && event.target.name?.startsWith("Value-1")) {
              this.resetGiftCardDropdown();
            }
          });
        });

        this.hideErrors = this.dataset.hideErrors === "true";
      }

      connectedCallback() {
        this.initSubmitButton();
        this.cartUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => {
          if (event.source === "product-form") return;
          this.onCartUpdate(event);
        });

        this.pageshowHandler = (event) => {
          if (!event.persisted || !this.itemAddedToCart || !this.submitButton) return;

          const variantId = this.submitButton.getAttribute("data-variant-id");
          if (!variantId) return;

          fetch("/cart.js")
            .then((res) => res.json())
            .then((cart) => {
              const stillInCart = cart.items.some((item) => String(item.variant_id) === String(variantId));
              if (!stillInCart) {
                this.resetSubmitButton();
              }
            })
            .catch((err) => {
              console.error("Cart check on pageshow failed:", err);
              captureErrorWithScope(err, {
                area: "cart",
                method: "pageshowHandler",
              });
            });
        };
        window.addEventListener("pageshow", this.pageshowHandler);
      }

      disconnectedCallback() {
        if (this.cartUpdateUnsubscriber) {
          this.cartUpdateUnsubscriber();
        }
        if (this.pageshowHandler) {
          window.removeEventListener("pageshow", this.pageshowHandler);
        }
      }

      handleGiftCardDropdownChange(selectElement) {
        const selectedOption = selectElement.selectedOptions[0];
        const variantId = selectedOption?.dataset?.variantId;

        // Uncheck radio buttons
        const radios = document.querySelectorAll('input[name^="Value-1"]');
        radios.forEach((radio) => {
          radio.checked = false;
        });

        // Update hidden variant ID input
        const variantInput = this.form.querySelector('input[name="id"]');
        if (variantInput && variantId) {
          variantInput.value = variantId;
          variantInput.disabled = false;
        }

        // Update URL with variant parameter
        if (variantId) {
          const url = new URL(window.location.href);
          url.searchParams.set("variant", variantId);
          window.history.replaceState({}, "", url.toString());
        }

        // Ensure submit button is enabled
        if (this.submitButton?.name === "add") {
          this.submitButton.removeAttribute("disabled");
        }
      }

      resetGiftCardDropdown() {
        const dropdown = document.getElementById("fp-gift-card-amount");
        if (dropdown) {
          dropdown.selectedIndex = 0;
        }
      }

      resetSubmitButton() {
        this.itemAddedToCart = false;
        this.submitButton.removeAttribute("disabled");
        this.submitButton.removeAttribute("aria-disabled");
        this.submitButton.classList.remove("disabled");
        this.submitButton.setAttribute("aria-label", "Add to Bag");
        this.submitButton.setAttribute("data-quantity", "0");
        const spanEl = this.submitButton.querySelector("span");
        if (spanEl) spanEl.textContent = "Add to Bag";
      }

      disableSubmitButton() {
        this.submitButton.setAttribute("disabled", true);
        this.submitButton.setAttribute("aria-disabled", true);
      }

      async initSubmitButton() {
        if (!isLocalDev()) await window.isClerkLoaded(); // NOTE: declared on theme.liquid
        if (this.submitButton?.name && this.submitButton.name === "add") {
          this.submitButton.removeAttribute("disabled");
        }
      }

      async onSubmitHandler(event) {
        event.preventDefault();
        this.itemAddedToCart = false;
        if (this.submitButton.getAttribute("aria-disabled") === "true") {
          return;
        }

        // Validate gift card recipient fields (email/date) before adding to cart
        const recipientForm = this.querySelector("recipient-form");
        if (recipientForm && typeof recipientForm.validateSendGiftForm === "function") {
          const isValid = recipientForm.validateSendGiftForm(event);
          if (isValid === false) {
            // Abort submission and reset loading state
            this.handleErrorMessage(); // hide generic errors; recipient form shows its own
            this.submitButton.classList.remove("loading");
            this.submitButton.removeAttribute("disabled");
            this.submitButton.removeAttribute("aria-disabled");
            const spinner = this.querySelector(".loading__spinner");
            if (spinner) spinner.classList.add("hidden--important");
            this.error = true;
            return;
          }
        }

        this.handleErrorMessage();

        this.disableSubmitButton();
        this.submitButton.classList.add("loading");
        this.querySelector(".loading__spinner").classList.remove("hidden--important");

        const config = fetchConfig("javascript");
        config.headers["X-Requested-With"] = "XMLHttpRequest";
        delete config.headers["Content-Type"];

        const formData = new FormData(this.form);

        const selectElement = this.querySelector("#fp-gift-card-amount");
        const selectedOption = selectElement?.selectedOptions[0];
        const sendAt = formData.get("properties[_gift_send_at]");

        if (sendAt) {
          const [year, month, day] = sendAt.split("-").map(Number);
          const sendAtDate = new Date(year, month - 1, day);
          const now = new Date();
          const todayDate = new Date(now.getFullYear(), now.getMonth(), now.getDate());

          if (sendAtDate > now) {
            formData.set("properties[_gift_send_at_display]", sendAt);
            formData.set("properties[_gift_send_at]", sendAtDate.toISOString().replace(/\.\d{3}Z$/, "-00:00"));
          }
          if (isNaN(sendAtDate.getTime()) || sendAtDate <= todayDate) {
            formData.delete("properties[_gift_send_at]");
          }
        }

        if (selectedOption) {
          const giftCardAmount = selectedOption.value;
          const variantId = selectedOption.dataset.variantId;
          if (giftCardAmount) {
            const amount = giftCardAmount.replace("$", "");
            formData.delete("id");
            formData.set("id", variantId);
            formData.append("Value-1\n", `$${amount}`);
          }
        }

        if (this.cart) {
          formData.append(
            "sections",
            this.cart.getSectionsToRender().map((section) => section.id),
          );
          formData.append("sections_url", window.location.pathname);
          this.cart.setActiveElement(document.activeElement);
        }

        try {
          config.body = formData;
          const responseReq = await fetch(`${routes.cart_add_url}`, config);
          const response = await responseReq.json();
          if (response.key) {
            const giftSendAt = formData.get("properties[_gift_send_at]");
            const recipientName = formData.get("properties[_recipient_name]");
            const recipientEmail = formData.get("properties[_recipient_email]");
            const giftImage = formData.get("properties[_gift_image]");
            const giftMessage = formData.get("properties[_gift_message]");

            processAddingItem({
              epi: response.variant_id,
              empi: response.product_id,
              du: this.submitButton.getAttribute("data-product-url"),
              qty: response.quantity,
              cprops: {
                ...(giftSendAt ? { _gift_send_at: giftSendAt } : {}),
                ...(recipientName ? { _recipient_name: recipientName } : {}),
                ...(recipientEmail
                  ? {
                      _recipient_email: recipientEmail,
                      _custom_epi: `${response.variant_id} custom`,
                      _custom_empi: `${response.product_id} custom`,
                    }
                  : {}),
                ...(giftImage ? { _gift_image: giftImage } : {}),
                ...(giftMessage ? { _gift_message: giftMessage } : {}),
              },
              ...(recipientEmail ? { price: response.price } : {}),
              ...(recipientEmail ? { key: response.key } : {}),
            });
          }
          if (response.status) {
            publish(PUB_SUB_EVENTS.cartError, {
              source: "product-form",
              productVariantId: formData.get("id"),
              errors: response.errors || response.description,
              message: response.message,
            });
            this.handleErrorMessage(response.description);

            const soldOutMessage = this.submitButton.querySelector(".sold-out-message");
            if (!soldOutMessage) {
              return;
            }
            this.submitButton.querySelector("span").classList.add("hidden--important");
            soldOutMessage.classList.remove("hidden--important");
            this.error = true;
            return;
          } else if (!this.cart) {
            window.location = window.routes.cart_url;
            return;
          }
          if (!this.error) {
            publish(PUB_SUB_EVENTS.cartUpdate, {
              source: "product-form",
              productVariantId: formData.get("id"),
              cartData: response,
            });
            // NOTE: Update the submit button's data-quantity attribute value if the item was added to the cart successfully.
            // This value is used in the persisted cart feature.
            const currentDataQuantity = this.submitButton.getAttribute("data-quantity");
            this.submitButton.setAttribute("data-quantity", Number(currentDataQuantity ?? 0) + 1);

            const updatedQuantity = Number(this.submitButton.getAttribute("data-quantity"));
            const inventoryQuantity = Number(this.submitButton.getAttribute("data-inventory-quantity"));
            if (inventoryQuantity > 0 && updatedQuantity >= inventoryQuantity) {
              // Disable button and update the button text to "Added to Bag"
              this.disableSubmitButton();
              this.submitButton.classList.add("disabled");
              this.submitButton.setAttribute("aria-label", "Added to Bag");
              const spanEl = this.submitButton.querySelector("span");
              if (spanEl) spanEl.textContent = "Added to Bag";
              this.itemAddedToCart = true;
            }
          }
          this.error = false;
          const quickAddModal = this.closest("quick-add-modal");
          if (quickAddModal) {
            document.body.addEventListener(
              "modalClosed",
              () => {
                setTimeout(() => {
                  this.cart.renderContents(response);
                });
              },
              { once: true },
            );
            quickAddModal.hide(true);
          } else {
            this.cart.renderContents(response);
          }
        } catch (error) {
          console.error(error);
        } finally {
          this.submitButton.classList.remove("loading");
          toggleCartLoader("hide");
          if (this.cart && this.cart.classList.contains("is-empty")) this.cart.classList.remove("is-empty");
          if (!this.error && !this.itemAddedToCart) {
            this.submitButton.removeAttribute("disabled");
            this.submitButton.removeAttribute("aria-disabled");
          }
          this.querySelector(".loading__spinner").classList.add("hidden--important");
        }
      }

      onCartUpdate(event) {
        if (!this.itemAddedToCart || !this.submitButton) return;

        const variantId = this.submitButton.getAttribute("data-variant-id");
        if (!variantId) return;

        const cartData = event.cartData;
        if (!cartData || !cartData.items) return;

        const stillInCart = cartData.items.some((item) => String(item.variant_id) === String(variantId));

        if (!stillInCart) {
          this.resetSubmitButton();
        }
      }

      handleErrorMessage(errorMessage = false) {
        if (this.hideErrors) return;

        this.errorMessageWrapper =
          this.errorMessageWrapper || this.querySelector(".product-form__error-message-wrapper");
        if (!this.errorMessageWrapper) return;
        this.errorMessage = this.errorMessage || this.errorMessageWrapper.querySelector(".product-form__error-message");

        this.errorMessageWrapper.toggleAttribute("hidden", !errorMessage);

        if (errorMessage) {
          this.errorMessage.textContent = errorMessage;
        }
      }

      getCart() {
        return fetch("/cart.js")
          .then((response) => {
            if (!response.ok) {
              throw new Error("Failed to fetch cart");
            }
            return response.json();
          })
          .then((data) => data)
          .catch((error) => {
            console.error("Cart fetch error:", error);
            return null;
          });
      }
    },
  );
}
